import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberMutationReadDataSchema } from '../members/response';
import { escalationContactAttemptOutcomeSchema } from './schema';

// ─── log an unsuccessful contact attempt ────────────────────────────────────
//
// Wire vocabulary for the care team recording that they tried to reach a
// member about an escalation and could not. The escalation stays open; the
// attempt is a fact appended beside it.

/**
 * The longest working note an attempt may carry. Long enough for "left
 * voicemail, will retry after 3pm"; short enough that the working note does
 * not quietly become a shadow clinical record. Exported so a client input can
 * bound itself from the same number.
 */
export const ESCALATION_CONTACT_ATTEMPT_NOTE_MAX_LENGTH = 500;

/**
 * Body of the staff POST that logs one attempt.
 *
 * `.strict()`: a REQUEST the server validates, so an unknown key is a client
 * bug worth rejecting (ADR-135). That includes any attempt to name the acting
 * clinician — who made the attempt comes from the authorized-staff gate, and a
 * body-supplied id would be an attribution forgery.
 *
 * A blank note is rejected rather than stored as null: sending whitespace is a
 * client that forgot to omit the field, and storing it would make "no note"
 * two different values.
 */
export const escalationContactAttemptRequestSchema = z
  .object({
    note: z.string().trim().min(1).max(ESCALATION_CONTACT_ATTEMPT_NOTE_MAX_LENGTH).optional(),
  })
  .strict();
export type EscalationContactAttemptRequest = z.infer<typeof escalationContactAttemptRequestSchema>;

/**
 * What the write returns: the outcome and the member WRITE echo, the same
 * `{ outcome, member }` shape as `escalationMarkHandledDataSchema` and for the
 * same reasons — read-your-writes without a second request, and a write echo
 * that a stale client parser can still accept.
 *
 * Deliberately NOT `.strict()`: Atlas parses through `schema.parse()`, which
 * throws on an unknown key, so a strict response would fail a submit on any
 * bundle older than the deploy that adds a field (ADR-135 Rule 1).
 *
 * The note is not echoed. It is internal, and nothing the caller needs.
 */
export const escalationContactAttemptDataSchema = z.object({
  outcome: escalationContactAttemptOutcomeSchema,
  member: memberMutationReadDataSchema,
});
export type EscalationContactAttemptData = z.infer<typeof escalationContactAttemptDataSchema>;

export const escalationContactAttemptResponseSchema = apiEnvelope(
  escalationContactAttemptDataSchema,
);
export type EscalationContactAttemptResponse = z.infer<
  typeof escalationContactAttemptResponseSchema
>;
